import { LitElement } from 'lit';
import css from './Input.css.js';
export class Input extends LitElement {
    constructor() {
        super(...arguments);
        this.type = 'text';
        this.value = '';
        this.hideLabel = false;
        this.checked = false;
        this.disabled = false;
    }
    /**
     * Base styling
     */
    static get styles() {
        return css;
    }
    /**
     * Since there is no way to return property metadata by using the property decorator
     * we use the static `properties` instead and use it in the `updateHiddenInput` method.
     */
    static get properties() {
        return {
            name: { type: String, reflect: true },
            value: { type: String, reflect: false },
            valid: { type: Boolean, reflect: true },
            checked: { type: Boolean, reflect: true },
            disabled: { type: Boolean, reflect: true },
            label: { type: String, reflect: true },
            hideLabel: { type: Boolean, reflect: true, attribute: 'hide-label' },
            type: { type: String, reflect: true },
        };
    }
    connectedCallback() {
        super.connectedCallback();
        this.init();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeHiddenInput();
    }
    /**
     * Change handler
     * - Invoke handleEvent
     * @param {Event} event
     */
    onChange(event) {
        this.handleEvent(event);
    }
    /**
     * Input handler
     * - Invoke handleEvent
     * @param {Event} event
     */
    onInput(event) {
        this.handleEvent(event);
        // Update hidden input value — not as attribute because rerenders break things like `defaultValue`
        if (this.hiddenInputElement) {
            const target = event.currentTarget;
            this.hiddenInputElement.value = target?.value || '';
        }
    }
    /**
     * Stop input bubbling
     * - Invoke handleEvent
     * @param {Event} event
     */
    stopPropagation(event) {
        event.stopPropagation();
    }
    /**
     * Initialize
     * - Create hidden input
     * - Append to parentNode in the light DOM
     */
    init() {
        this.hiddenInputElement = this.createHiddenInput();
        this.appendChild(this.hiddenInputElement);
        this.refreshHiddenInput();
    }
    /**
     * Invoke when an attribute value changed
     * @param { String } name
     * @param { String } oldValue
     * @param { String } newValue
     */
    attributeChangedCallback(name, oldValue, newValue) {
        super.attributeChangedCallback(name, oldValue, newValue);
        if (name === 'type') {
            // Create a new hidden input when type changes
            this.createHiddenInput();
        }
        else {
            this.updateHiddenInput(name, newValue);
        }
    }
    /**
     * Generic event handler
     * - Dispatch event to the light DOM
     * - Updates the hidden input
     * @param {Event} event
     */
    handleEvent(event) {
        const inputElement = event.currentTarget;
        if (inputElement) {
            this.value = inputElement.value;
            this.checked = inputElement.checked;
        }
        this.dispatchEvent(new Event(event.type));
    }
    /**
     * Create hidden Input
     * @returns { HTMLInputElement } the hidden Input
     */
    createHiddenInput() {
        const hiddenInput = document.createElement('input');
        hiddenInput.type = this.type;
        hiddenInput.style.position = 'fixed';
        hiddenInput.style.visibility = 'hidden';
        hiddenInput.style.height = '0';
        hiddenInput.tabIndex = -1;
        hiddenInput.addEventListener('change', this.stopPropagation);
        hiddenInput.addEventListener('input', this.stopPropagation);
        return hiddenInput;
    }
    /**
     * Remove hidden Input
     */
    removeHiddenInput() {
        const hiddenInput = this.hiddenInputElement;
        if (!hiddenInput) {
            return;
        }
        hiddenInput.removeEventListener('change', this.stopPropagation);
        hiddenInput.removeEventListener('input', this.stopPropagation);
        this.removeChild(hiddenInput);
        this.hiddenInputElement = undefined;
    }
    /**
     * Update the checked attribute of the hidden Input
     * @param { String } attribute
     * @param { String | null } value
     */
    updateHiddenInput(attribute, value) {
        const hiddenInput = this.hiddenInputElement;
        if (!hiddenInput) {
            return;
        }
        if (value == null) {
            hiddenInput.removeAttribute(attribute);
            return;
        }
        if (!Input.properties[attribute])
            return;
        const { type } = Input.properties[attribute];
        if (type === Boolean) {
            hiddenInput.setAttribute(attribute, '');
        }
        else {
            hiddenInput.setAttribute(attribute, value);
        }
    }
    /**
     * Loop through all the declared properties and update the hidden input
     */
    refreshHiddenInput() {
        Object.keys(Input.properties).forEach((propertyKey) => {
            this.updateHiddenInput(propertyKey, this.getAttribute(propertyKey));
        });
    }
}
